import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useLocation } from 'react-router-dom';

import Navigation, { getNavData, splitsForNav, NavigationFunnel } from '@nebula/navigation';
import nebulaTheme from '@nebula/theme';
import Analytics, { InteractionInputEvent } from '@ecs-uk/web-common-analytics';
import getAlertCountAction from '../../actions/alerts/getCount';
import { getEnv } from '../Context/env';

const GlobalNavStyles = createGlobalStyle`
    ul[aria-label="My account links"] {
      margin-block-start: 0;
      margin-block-end: 0;
      padding-inline-start: 0;

      a {
        font-weight: 400;
      }
    }

    [data-testid='inav-links-mobile']:empty {
      display: none;
    }
  `;

const LogoAndProfileNav = styled.div`
  [data-testid='inav-desktop-navigation'] > div,
  [data-testid='inav-mobile-topmenu'] {
    min-height: 4.5rem;
  }
`;

const NavigationWrapper = ({
  splits,
  getAlertCount,
  alerts,
  subscriptionDetails,
  productDetails,
  currentUser,
  boostStatus,
  isSecurePage,
  isErrorPage,
}) => {
  const location = useLocation();
  const env = getEnv();

  useEffect(() => {
    if (!isErrorPage && !isSecurePage) {
      getAlertCount();
    }
  }, [isErrorPage, isSecurePage]);

  const isChooserRoute = location.pathname.startsWith('/chooser');

  const splitList = Object.keys(splits)
    .filter(split => splitsForNav.includes(split))
    .map(splitKey => ({ splitName: splitKey, treatment: splits[splitKey] ? 'on' : 'off' }));

  let data;
  if (!subscriptionDetails.current) {
    data = {
      splitNamesAndTreatments: [],
      domain: window.location.origin,
      customerInformation: {
        family: 'basic',
      },
    };
  } else {
    data = {
      alertsInformation: {
        count: alerts.unread,
      },
      boostInformation: {
        hasProposition: boostStatus?.hasProposition,
      },
      customerInformation: {
        benefits: subscriptionDetails.current.benefits,
        family: subscriptionDetails.current.family,
        isWithinTrial: productDetails.isFreeTrial,
        name: {
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
        },
      },
      domain: window.location.origin,
      splitNamesAndTreatments: splitList,
    };
  }

  const navData = getNavData(data);
  const raiseEcd2 = event => Analytics.publish(InteractionInputEvent.fromObject(event));

  if (isChooserRoute) {
    return (
      <ThemeProvider theme={nebulaTheme}>
        <NavigationFunnel
          data={{
            ...navData,
            signOut: {
              automation: 'inav-logout',
              href: `${env.REACT_APP_LOGOUT_URL}`,
              id: 'signout',
              label: 'Logout',
            },
          }}
          zIndex={11}
        />
      </ThemeProvider>
    );
  }

  if (isErrorPage || isSecurePage) {
    const profileNavData = getNavData({
      ...data,
      show2024Nav: true,
    });

    // Only the logo and the profile icon are visible.
    return (
      <ThemeProvider theme={nebulaTheme}>
        <GlobalNavStyles />
        <LogoAndProfileNav>
          <Navigation
            data={{
              ...profileNavData,
              alerts: false,
              helpSupport: [],
              links: [],
              show2024Nav: true,
              showAvatarProfileMvp: true,
            }}
            zIndex={5}
            raiseEcd2={raiseEcd2}
          />
        </LogoAndProfileNav>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={nebulaTheme}>
      <GlobalNavStyles />
      <Navigation data={navData} zIndex={5} raiseEcd2={raiseEcd2} />
    </ThemeProvider>
  );
};

NavigationWrapper.propTypes = {
  alerts: PropTypes.shape({
    unread: PropTypes.number,
    error: PropTypes.bool.isRequired,
  }).isRequired,
  getAlertCount: PropTypes.func.isRequired,
  splits: PropTypes.shape({}),
  boostStatus: PropTypes.shape({
    hasProposition: PropTypes.bool,
  }),
  currentUser: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
  subscriptionDetails: PropTypes.shape({
    current: PropTypes.shape({
      benefits: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          benefitActivated: PropTypes.bool,
        })
      ),
      family: PropTypes.string,
    }),
  }),
  productDetails: PropTypes.shape({
    isFreeTrial: PropTypes.bool,
  }),
  isSecurePage: PropTypes.bool,
  isErrorPage: PropTypes.bool,
};

NavigationWrapper.defaultProps = {
  splits: {},
  boostStatus: {},
  currentUser: {},
  subscriptionDetails: {},
  productDetails: {},
  isSecurePage: false,
  isErrorPage: false,
};

const mapReduxStateToProps = state => ({
  alerts: state.alerts,
  splits: state.split.treatments,
  boostStatus: state.boost.data,
  currentUser: state.auth.currentUser,
  productDetails: state.productDetails,
  subscriptionDetails: state.subscriptions,
  isSecurePage: state.app.isSecurePage,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getAlertCount: getAlertCountAction,
    },
    dispatch
  );

export default connect(mapReduxStateToProps, mapDispatchToProps)(NavigationWrapper);
